
import { FormControl, FormGroup, Validators, ValidatorFn, AsyncValidator, AsyncValidatorFn, AbstractControl } from '@angular/forms';
import { DynamicQuestions } from './services/question/question.service';
import { QuestionRow } from './question-row';
import { ModalQuestion } from './question-modal';

export enum QuestionVisiblityOperators{
  EQUAL,
  LESS_THAN,
  GREATER_THAN,
};

export enum QuestionUITypes{
  ERROR = -1,
  STRING,
  INTEGER,
  DECIMAL,
  DROPDOWN,
  BOOLEAN,
  ROW,
  BUTTON,
  COL2,
  MODAL,
  MODALBUTTON
}

export enum QuestionRequestTypes{
  ERROR = -1,
  STRING,
  INTEGER,
  DECIMAL,
  BOOLEAN
}

export interface QuestionVisiblityOperation{
  key: string,
  operator: QuestionVisiblityOperators,
  value: string,
}

export interface QuestionBaseParams <T>{
  standardValue: string,
  key: string,
  keyCryptic: string,
  label: string,
  required?: boolean,
  divider?: string;
  visibilityOperations?: Array<QuestionVisiblityOperation>,
  requestType: QuestionRequestTypes,
  displayTextPosition?: string,
  modalitems?: Array<QuestionElement>,  
  executeId?: string
}

export type QuestionElement = QuestionBase<any> & ModalQuestion;

export abstract class QuestionBase<T>{
  public standardValue: string;
  public value: string;
  public key: string;
  keyCryptic: string;
  public label: string;
  public required: boolean;
  public divider?: string;
  public modalitems?: Array<QuestionElement>;
  public executeId?: string;
  public visibilityOperations?: Array<QuestionVisiblityOperation>;
  public questionType: QuestionUITypes;
  public requestType: QuestionRequestTypes;
  public displayTextPosition?: string;
  public visible = true;
  public controlAssociated : FormControl;
  public prev : QuestionElement;
  public next : QuestionElement;

  private dirty: boolean = false;

  constructor(options: QuestionBaseParams<T>) {
    this.key = options.key;
    this.label = options.label;
    this.required = !!options.required;
    this.divider = options.divider;
    this.visibilityOperations = options.visibilityOperations || [];
    this.requestType = options.requestType;
    this.displayTextPosition = options.displayTextPosition;
    this.modalitems = options.modalitems;
    this.executeId = options.executeId;
    this.setStandardValue( options.standardValue );
  }

  public static questionUITypeFromString( str : string ): QuestionUITypes{
    let questionType : QuestionUITypes;

    switch(str){
      case 'text':
        questionType = QuestionUITypes.STRING;
      break;
      case 'integer':
        questionType = QuestionUITypes.INTEGER;
      break;
      case 'decimal':
        questionType = QuestionUITypes.DECIMAL;
      break;
      case 'selectfield':
        questionType = QuestionUITypes.DROPDOWN;
      break;
      case 'boolean':
        questionType = QuestionUITypes.BOOLEAN;
      break;
      case 'row':
        questionType = QuestionUITypes.ROW;
      break;
      case 'button':
        questionType = QuestionUITypes.BUTTON;
      break;
      case 'col2':
        questionType = QuestionUITypes.COL2;
      break;
      case 'modal':
        questionType = QuestionUITypes.MODAL;
      break;
      case 'modalButton':
        questionType = QuestionUITypes.MODALBUTTON;
      break;
      default:
        questionType = QuestionUITypes.ERROR;
      break;
    }

    return questionType;
  }

  public static requestTypeFromString( str : string ): QuestionRequestTypes{
    let requestType : QuestionRequestTypes;

    switch(str){
      case 'string':
        requestType = QuestionRequestTypes.STRING;
      break;
      case 'integer':
        requestType = QuestionRequestTypes.INTEGER;
      break;
      case 'decimal':
        requestType = QuestionRequestTypes.DECIMAL;
      break;
      case 'boolean':
        requestType = QuestionRequestTypes.BOOLEAN;
      break;
      default:
        requestType = QuestionRequestTypes.ERROR;
      break;
    }

    return requestType;
  }

  public getAvailableValue() : string{
    if( this.hasValue() ){
      return this.getValue();
    }else if( this.hasStandardValue() ){
      return this.getStandardValue();
    }else{
      return '';
    }
  }

  public abstract getValueForRequest();

  public setLiveValue( val : string ){
    return this.controlAssociated.setValue( val );;
  }

  public getLiveValue() : string{
    return this.controlAssociated.value;
  }

  public hasLiveValue() : boolean {
    return !!this.controlAssociated;
  }

  public hasValue() : boolean{
    return this.getValue() !== undefined;
  }

  public setValue( val : string ){
    this.value = val;
  }

  public getValue() : string{
    return this.value;
  }

  public hasStandardValue() : boolean {
    return this.getStandardValue() !== undefined;
  }

  public setStandardValue( val : string ){
    this.standardValue = val;
  }

  public getStandardValue() : any{
    return this.standardValue;
  }

  public setDirty(){
    this.dirty = true;
  }

  public clearDirty(){
    this.dirty = false;
  }

  public getDirty(){
    return this.dirty;
  }

  public liveChange( formGroup: FormGroup, control: FormControl, questions: DynamicQuestions){
    let val : any = control.value;
    if( this.key.substring(0,12) === 'geburtsdatum' ){
      //val = control.value.day + '.' + control.value.month + '.' + control.value.year;
    }

    //this.setValueString( val );

    if( this.key === 'monatsbruttoentgelt' ){
      if( 'bruttoRvPflichtig' in questions && !questions['bruttoRvPflichtig'].getDirty() ){
        formGroup.controls['bruttoRvPflichtig'].setValue(val);
      }

      if( 'bruttoKvPflichtig' in questions && !questions['bruttoKvPflichtig'].getDirty() ){
        formGroup.controls['bruttoKvPflichtig'].setValue(val);
      }

      if( 'bruttoSteuerPflichtig' in questions && !questions['bruttoSteuerPflichtig'].getDirty() ){
        formGroup.controls['bruttoSteuerPflichtig'].setValue(val);
      }
    }

    this.visibilityOperations.forEach(( operation ) => {
      let targetFormControl : AbstractControl = formGroup.controls[operation.key];
      let targetQuestion : QuestionBase<any> = questions[operation.key];
      let result: boolean;
      if( targetFormControl === undefined || targetQuestion === undefined ){
        console.error( 'bad error' );
      }

      if(this.visible === false){
        /* If the current question is invisible,
         * we don't care about it's children
         */
        targetQuestion.visible = false;
        targetFormControl.disable();
        return;
      }

      result = this.isVisible(operation);

      if( result ){
        targetQuestion.visible = true;
        targetFormControl.enable();
      }else{
        targetQuestion.visible = false;
        targetFormControl.disable();
      }
    });
  }

  protected isVisible(operation : QuestionVisiblityOperation) : any{
    console.error( 'this case isn\'t supposed to call isVisible' );
  }

  public getValidators() : Array<ValidatorFn>{
    let validators : Array<ValidatorFn> = [];

    if( this.required ){
      validators.push(Validators.required);
    }
    return validators;
  }
}


// WEBPACK FOOTER //
// ./src/app/question-base.ts